import { useState } from "react";
import {
  FaGithub,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaEnvelope,
} from "react-icons/fa";
import profile from "../../../images/profile.jpeg";
import resumePDF from "../../../assets/istiliCV.pdf";

const socials = [
  {
    id: 1,
    icon: <FaGithub />,
    link: "https://github.com/istili",
    label: "GitHub",
  },
  {
    id: 2,
    icon: <FaLinkedinIn />,
    link: "https://www.linkedin.com/in/ikram-stili-858648234/",
    label: "LinkedIn",
  },
];

const Sidebar = () => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  // Handle email copy to clipboard
  const handleEmailCopy = async () => {
    try {
      await navigator.clipboard.writeText("istili.ikram@example.com");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy email:", err);
    }
  };

  // Handle resume download with proper error handling
  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    
    try {
      const response = await fetch(resumePDF);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Stili_Ikram_Resume.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      setDownloadError("Failed to download resume. Please try again.");
      // Fallback: open in new tab
      setTimeout(() => {
        window.open(resumePDF, "_blank");
      }, 500);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {/* Sticky wrapper for sidebar */}
      <div className="sticky top-24 self-start z-10">
        {/* Main Sidebar Container - Glassmorphism + Neubrutalism */}
        <aside
          className="relative w-full rounded-2xl overflow-hidden group"
          data-scroll-reveal
        >
        {/* Base Glass Layer */}
        <div className="absolute inset-0 bg-white/80 backdrop-blur-md" />

        {/* Gradient Border Effect */}
        <div className="absolute inset-0 rounded-2xl p-[2px] bg-gradient-to-br from-green-400/30 via-emerald-400/10 to-blue-400/20" />

        {/* Content Wrapper */}
        <div className="relative z-10 p-6 md:p-8 rounded-2xl border border-white/40 shadow-xl shadow-green-500/5">
          {/* Top-right accent dot */}
          <div className="absolute top-4 right-4 w-3 h-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full shadow-lg shadow-green-400/50 animate-pulse" />

          {/* Status Badge */}
          <div className="flex items-center justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-semibold text-green-700">
                Open to Work
              </span>
            </div>
          </div>

          {/* Profile Image with Ring */}
          <div className="flex justify-center mb-6">
            <div className="relative group/image">
              {/* Gradient ring background */}
              <div className="absolute inset-0 -p-1 bg-gradient-to-br from-green-400 via-emerald-400 to-blue-400 rounded-full blur-sm group-hover/image:blur-md transition-all duration-300 opacity-75 group-hover/image:opacity-100" />

              {/* Actual image */}
              <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl group-hover/image:shadow-2xl group-hover/image:shadow-green-400/30 transition-all duration-300 group-hover/image:rotate-3">
                <img
                  src={profile}
                  alt="Stili Ikram"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="text-center mb-6">
            <div className="inline-block px-3 py-1 mb-4 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200">
              <p className="text-xs md:text-sm font-semibold text-green-700">
                Full Stack Developer
              </p>
            </div>
            <p className="text-xs md:text-sm text-gray-600">
              at{" "}
              <a
                href="https://42.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 font-semibold hover:underline transition-colors"
              >
                42 School
              </a>
            </p>
          </div>

          {/* Gradient Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-green-400/30 to-transparent mb-6" />

          {/* Contact Info */}
          <div className="space-y-3 mb-6">
            {/* Location */}
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <FaMapMarkerAlt className="text-green-600 flex-shrink-0" />
              <span>Khouribga, Morocco</span>
            </div>

            {/* Email - Clickable copy */}
            <button
              onClick={handleEmailCopy}
              className="w-full flex items-center gap-3 text-sm text-gray-700 hover:text-green-600 transition-colors group/email"
            >
              <FaEnvelope className="text-green-600 flex-shrink-0 group-hover/email:scale-110 transition-transform" />
              <div className="text-left flex-1">
                <span className="block text-xs text-gray-500">Email</span>
                <span className="block group-hover/email:text-green-600 transition-colors">
                  {copied ? "Copied! ✓" : "stilitaikram@gmail.com"}
                </span>
              </div>
            </button>
          </div>

          {/* Gradient Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-green-400/30 to-transparent mb-6" />

          {/* Social Links */}
          <div className="flex justify-center gap-4 mb-6">
            {socials.map((social) => (
              <a
                key={social.id}
                href={social.link}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="group/social relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg blur-md opacity-0 group-hover/social:opacity-75 transition-opacity duration-300" />
                <div className="relative w-11 h-11 rounded-lg bg-gradient-to-br from-green-100 to-blue-100 border border-green-200/50 text-green-700 flex items-center justify-center text-lg hover:scale-110 hover:shadow-lg hover:shadow-green-400/40 transition-all duration-300">
                  {social.icon}
                </div>
              </a>
            ))}
          </div>

          {/* Download CV Button - Fixed with shimmer effect */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="relative w-full group/btn overflow-hidden"
          >
            {/* Shimmer effect background */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover/btn:opacity-40 translate-x-full group-hover/btn:translate-x-0 transition-all duration-500 -skew-x-12" />

            {/* Button */}
            <div className="relative px-4 py-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold text-sm shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/50 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300">
              {downloading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Downloading...
                </span>
              ) : (
                "Download Resume"
              )}
            </div>
          </button>

          {/* Error message */}
          {downloadError && (
            <p className="mt-2 text-xs text-red-600 text-center">{downloadError}</p>
          )}

          {/* About Me Section */}
          <div className="mt-6 pt-6 border-t border-gray-200/50">
            <h3 className="text-sm md:text-base font-bold text-gray-900 mb-3">
              About Me
            </h3>
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed font-light mb-3">
              Student at 42 School, beyond Common Core curriculum. Learning through
              project-based, peer-led education with zero traditional lectures. Strong in
              self-discipline, debugging, and rapid technology adoption.
            </p>
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed font-light">
              <strong className="text-gray-900">Skills:</strong> C, C++, JS/TS, Shell
              scripting, Git, algorithms, memory management, network programming.
            </p>
          </div>
      </div>
    </aside>
      </div>
    </>
  );
};

export default Sidebar;
